import {
  HHStack,
  HHTextField,
  HHTooltip,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Paper } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useHingeSelectClient } from '../../api';
import { ProviderAccount } from '../../api/types';
import { StatusBadge } from '../components/badges/status-badge';
import { DisabledInfo, StepNav } from '../components/layout/step-nav';
import { TinTabs } from '../components/layout/tin-tabs';
import { SelectableCard } from '../components/selection/selectable-card';
import { ContractInput } from '../context/types';
import { useStepHeader } from '../context/wizard-header.context';
import { useWizardState } from '../context/wizard-state.context';
import { getOrgGroups, OrgGroup } from '../utils/org-groups';

const monoText = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 13,
};

interface CustomerAccountOrgCardProps {
  group: OrgGroup;
  defaultLegalName: string;
}

// Per-TIN ProviderAccount lookup + reuse/create decision. Single-TIN input
// renders one card identical to the pre-PR-2 layout; multi-TIN input renders
// one card per distinct TIN, each with its own search state and decision.
const CustomerAccountOrgCard = ({
  group,
  defaultLegalName,
}: CustomerAccountOrgCardProps): JSX.Element => {
  const { state, setState } = useWizardState();
  const client = useHingeSelectClient();
  const tin = group.tax_id;
  const fired = useRef(false);
  const [search, setSearch] = useState<{
    status: 'pending' | 'found' | 'not_found' | 'error';
    account?: ProviderAccount;
    error?: string;
  }>({ status: 'pending' });

  const idempotencyKey = `provider-account:${tin}`;

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    void (async (): Promise<void> => {
      try {
        const result = await client.getProviderAccount({
          providerAccountIdentifier: { organizationFein: tin },
        });
        if (result.ok) {
          setSearch({ status: 'found', account: result.data.providerAccount });
        } else {
          setSearch({ status: 'not_found' });
        }
      } catch (e) {
        setSearch({ status: 'error', error: (e as Error).message });
      }
    })();
  }, [client, tin]);

  const decision = state.decisions.customerAccount[tin];

  // Default to reusing the existing ProviderAccount when the lookup finds one
  // and the operator hasn't already chosen.
  useEffect(() => {
    if (decision != null) return;
    if (search.status !== 'found') return;
    const account = search.account;
    if (account == null) return;
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        customerAccount: {
          ...s.decisions.customerAccount,
          [tin]: {
            mode: 'reuse',
            id: account.providerAccountId ?? '',
            name: account.organization?.legalName ?? defaultLegalName,
          },
        },
      },
    }));
  }, [decision, search, setState, defaultLegalName, tin]);

  const useExisting = (): void => {
    if (search.status !== 'found' || !search.account) return;
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        customerAccount: {
          ...s.decisions.customerAccount,
          [tin]: {
            mode: 'reuse',
            id: search.account?.providerAccountId ?? '',
            name: search.account?.organization?.legalName ?? defaultLegalName,
          },
        },
      },
    }));
  };

  const useCreate = (): void => {
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        customerAccount: {
          ...s.decisions.customerAccount,
          [tin]: {
            mode: 'create',
            name: defaultLegalName,
            taxId: tin,
          },
        },
      },
    }));
  };

  const status = search.status;

  return (
    <HHStack spacing={2}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <HHStack spacing={2}>
          <HHStack direction="row" spacing={3} alignItems="center">
            <StatusBadge
              status={
                status === 'pending'
                  ? 'pending'
                  : status === 'found'
                    ? 'existing'
                    : status === 'error'
                      ? 'error'
                      : 'new'
              }
            />
            <HHTypography hhVariant="subtitle1">
              {defaultLegalName}
            </HHTypography>
            <HHTypography hhVariant="body2" sx={monoText}>
              TIN {tin}
            </HHTypography>
          </HHStack>
          {search.status === 'found' && search.account && (
            <HHStack spacing={1}>
              <HHTypography hhVariant="body">
                Found existing ProviderAccount:{' '}
                <strong>
                  {search.account.organization?.legalName ?? '(unnamed)'}
                </strong>
              </HHTypography>
              <HHTypography hhVariant="caption" sx={monoText}>
                providerAccountId: {search.account.providerAccountId}
              </HHTypography>
            </HHStack>
          )}
          {search.status === 'not_found' && (
            <HHTypography hhVariant="body">
              No existing ProviderAccount for this TIN.
            </HHTypography>
          )}
          {search.status === 'error' && (
            <HHTypography hhVariant="body" color="error">
              Lookup failed: {search.error}
            </HHTypography>
          )}
        </HHStack>
      </Paper>

      <div role="radiogroup" aria-label="Customer account decision">
        <HHStack spacing={1}>
          {search.status === 'found' && search.account && (
            <SelectableCard
              selected={decision?.mode === 'reuse'}
              onClick={useExisting}
              recommended
              recommendedLabel="Recommended · existing TIN match"
            >
              <HHTypography hhVariant="body1">Reuse existing</HHTypography>
              <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                Attach this contract to{' '}
                <strong>
                  {search.account.organization?.legalName ?? '(unnamed)'}
                </strong>
                . No new ProviderAccount will be created.
              </HHTypography>
            </SelectableCard>
          )}
          <SelectableCard
            selected={decision?.mode === 'create'}
            onClick={useCreate}
          >
            <HHTypography hhVariant="body1">Create new</HHTypography>
            <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
              Mint a fresh ProviderAccount for this TIN. Idempotency key keeps
              re-runs safe.
            </HHTypography>
          </SelectableCard>
        </HHStack>
      </div>

      {decision?.mode === 'create' && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <HHStack spacing={2}>
            <HHTextField
              hhVariant="variant-bypass"
              label="Account name"
              value={decision.name}
              onChange={(e): void =>
                setState(s => {
                  const cur = s.decisions.customerAccount[tin];
                  if (cur?.mode !== 'create') return s;
                  return {
                    ...s,
                    decisions: {
                      ...s.decisions,
                      customerAccount: {
                        ...s.decisions.customerAccount,
                        [tin]: { ...cur, name: e.target.value },
                      },
                    },
                  };
                })
              }
              fullWidth
            />
            <HHStack direction="row" spacing={1} alignItems="center">
              <HHTypography hhVariant="caption" sx={{ opacity: 0.6 }}>
                Idempotency key:
              </HHTypography>
              <HHTypography hhVariant="caption" sx={monoText}>
                {idempotencyKey}
              </HHTypography>
              <HHTooltip
                hhVariant="variant-bypass"
                placement="top"
                title="Stable key derived from TIN. Re-running this step with the same TIN will not create a duplicate account."
              >
                <span style={{ opacity: 0.5, cursor: 'help' }}>(?)</span>
              </HHTooltip>
            </HHStack>
          </HHStack>
        </Paper>
      )}
    </HHStack>
  );
};

interface CustomerAccountInnerProps {
  input: ContractInput;
}

const CustomerAccountInner = ({
  input,
}: CustomerAccountInnerProps): JSX.Element => {
  const { state } = useWizardState();
  const orgGroups = getOrgGroups(input);
  const [activeTin, setActiveTin] = useState<string>(
    () => orgGroups[0]?.tax_id ?? '',
  );

  useStepHeader({
    description:
      orgGroups.length > 1
        ? `Resolve a ProviderAccount for each of the ${orgGroups.length} legal entities. Each Contract attaches to its own ProviderAccount.`
        : 'The legal counterparty Hinge pays. Reuse an existing ProviderAccount when possible; otherwise create a new one with a deterministic idempotency key.',
  });

  const isReadyForTin = (tin: string): boolean =>
    state.decisions.customerAccount[tin] != null;

  const canContinue = orgGroups.every(g => isReadyForTin(g.tax_id));
  const disabledInfo: DisabledInfo | undefined = canContinue
    ? undefined
    : {
        message:
          orgGroups.length > 1
            ? 'Choose Reuse existing or Create new for each TIN before continuing.'
            : 'Choose to reuse an existing customer account or create a new one.',
        severity: 'info',
      };

  return (
    <HHStack spacing={3}>
      <TinTabs
        items={orgGroups.map(g => ({
          group: g,
          legalName:
            state.decisions.orgNames[g.tax_id] ?? input.entity_legal_name,
          ready: isReadyForTin(g.tax_id),
        }))}
        activeTin={activeTin}
        onSelect={setActiveTin}
      />

      {orgGroups.map(g => {
        const isActive = g.tax_id === activeTin || orgGroups.length === 1;
        if (!isActive) return null;
        return (
          <CustomerAccountOrgCard
            key={g.tax_id}
            group={g}
            defaultLegalName={
              state.decisions.orgNames[g.tax_id] ?? input.entity_legal_name
            }
          />
        );
      })}

      <StepNav
        id="customer-account"
        canContinue={canContinue}
        disabledInfo={disabledInfo}
      />
    </HHStack>
  );
};

export const CustomerAccountStep = (): JSX.Element => {
  const { state } = useWizardState();
  if (state.input == null) return <Navigate to="../intake" replace />;
  return <CustomerAccountInner input={state.input} />;
};
